"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  trackAddToCart,
  trackRemoveFromCart,
} from "@/lib/analytics/ecommerce";
import { resolveProductSku } from "@/lib/resolveProductSku";
import { buildResolvedCartItem } from "@/lib/agentCartItem";

/* ── Types ── */
export type CartItem = {
  id: string;
  name: string;
  variant: string;
  price: number;
  quantity: number;
  image: string;
  variationId?: number;
  sku?: string;
  maxQty?: number;
  tierPrices?: { qty: number; price: number }[];
  dosageForm?: string;
  isGeneric?: boolean;
  requiresFridge?: boolean;
  /** Product needs a pharmacist-approved prescription before it can be filled. */
  requiresPrescription?: boolean;
  /** Species this drug is approved for (e.g. ["Human"], ["Dog", "Cat"]). */
  species?: string[];
  /** ISO3 country code (e.g. "CAN", "USA") — drives the SVG flag in cart UIs. */
  countryCode?: string;
  /** Product slug for linking back to the medication detail page. */
  productSlug?: string;
};

type CartContextValue = {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: Omit<CartItem, "quantity">, requestedQty?: number) => boolean;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  totalItems: number;
  subtotal: number;
};

const STORAGE_KEY = "bp-cart";

const CartContext = createContext<CartContextValue | null>(null);

/* ── Helpers ── */
function resolvePrice(
  item: Partial<CartItem>,
  qty: number,
): number {
  if (item.tierPrices?.length) {
    let best = item.tierPrices[0];
    for (const t of item.tierPrices) {
      if (qty >= t.qty) best = t;
    }
    return best.price;
  }
  return item.price ?? 0;
}

/**
 * Country names known to appear in the variant string. Used to backfill
 * `countryCode` on cart entries persisted before we started storing it
 * alongside the variant text. Keep aligned with the `CountryFlag` module.
 */
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  Canada: "CAN",
  "United States": "USA",
  "United Kingdom": "GBR",
  India: "IND",
  Australia: "AUS",
  "New Zealand": "NZL",
  Turkey: "TUR",
  Cambodia: "KHM",
  Singapore: "SGP",
  Mauritius: "MUS",
};

/**
 * Migrate a persisted cart entry to the current shape. Older entries embed
 * the country as a flag emoji inside the variant string and don't carry
 * a separate `countryCode`. We strip the emoji and try to backfill the code
 * from the country name so the SVG flag in the cart UIs still renders for
 * legacy items. `productSlug` can't be recovered — those entries will stay
 * non-clickable until removed and re-added.
 */
function migrateLegacyItem(raw: CartItem): CartItem {
  const stripped = raw.variant
    ?.replace(/[\u{1F1E6}-\u{1F1FF}]{2}/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim() ?? "";
  let countryCode = raw.countryCode;
  if (!countryCode && stripped) {
    for (const [name, code] of Object.entries(COUNTRY_NAME_TO_CODE)) {
      if (stripped.includes(name)) {
        countryCode = code;
        break;
      }
    }
  }
  return { ...raw, variant: stripped, countryCode };
}

function loadCart(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    return Array.isArray(parsed) ? parsed.map(migrateLegacyItem) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota exceeded — silently ignore */
  }
}

/* ── Provider ── */
export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  /* Hydrate from localStorage after mount */
  useEffect(() => {
    setItems(loadCart());
    setHydrated(true);
  }, []);

  /* Persist whenever items change (skip initial SSR render) */
  useEffect(() => {
    if (hydrated) saveCart(items);
  }, [items, hydrated]);

  /*
   * One-time self-heal for legacy agent items. Older builds stored agent adds
   * as `agent-<slug>` with whatever sku the voice agent supplied — sometimes
   * the slug itself, which the order API rejects ("Drug Package ID is not
   * valid"). The frontend is now the single source of truth, so re-resolve any
   * such entry from its slug into the canonical, manual-add-identical shape.
   * Runs once after hydration; unresolvable entries are left untouched (no
   * silent data loss).
   */
  const healedRef = useRef(false);
  useEffect(() => {
    if (!hydrated || healedRef.current) return;
    healedRef.current = true;
    const legacy = items.filter((i) => i.id.startsWith("agent-"));
    if (legacy.length === 0) return;

    let active = true;
    (async () => {
      for (const stale of legacy) {
        const slug = stale.productSlug || stale.id.slice("agent-".length);
        let seed: Omit<CartItem, "quantity"> | null = null;
        try {
          seed = buildResolvedCartItem(slug, await resolveProductSku(slug));
        } catch {
          seed = null;
        }
        if (!active) return;
        if (!seed) continue;
        const resolved = seed;
        setItems((prev) => {
          const without = prev.filter((i) => i.id !== stale.id);
          const existing = without.find((i) => i.id === resolved.id);
          if (existing) {
            return without.map((i) =>
              i.id === resolved.id
                ? { ...i, quantity: i.quantity + stale.quantity }
                : i,
            );
          }
          return [...without, { ...resolved, quantity: stale.quantity }];
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [hydrated, items]);

  const addItem = useCallback(
    (item: Omit<CartItem, "quantity">, requestedQty = 1): boolean => {
      if (!item.sku) {
        console.warn(
          `[Cart] Rejected add-to-cart for "${item.name}" — missing SKU (npharmPackageId).`,
        );
        return false;
      }
      let finalQty = requestedQty;
      let finalPrice = resolvePrice(item, requestedQty);
      setItems((prev) => {
        const existing = prev.find((i) => i.id === item.id);
        if (existing) {
          let newQty = existing.quantity + requestedQty;
          if (item.maxQty && newQty > item.maxQty) newQty = item.maxQty;
          const price = resolvePrice(item, newQty);
          finalQty = newQty;
          finalPrice = price;
          // Merge `item` first so fields added in newer versions of the schema
          // (countryCode, productSlug, etc.) backfill onto entries persisted
          // before those fields existed. Quantity + price are recomputed and
          // overwrite anything that came in on `item`.
          return prev.map((i) =>
            i.id === item.id ? { ...i, ...item, quantity: newQty, price } : i,
          );
        }
        let qty = requestedQty;
        if (item.maxQty && qty > item.maxQty) qty = item.maxQty;
        const price = resolvePrice(item, qty);
        finalQty = qty;
        finalPrice = price;
        return [...prev, { ...item, price, quantity: qty }];
      });
      trackAddToCart({ ...item, quantity: finalQty, price: finalPrice });
      return true;
    },
    [],
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => {
      const removed = prev.find((i) => i.id === id);
      if (removed) trackRemoveFromCart(removed);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        let q = quantity;
        if (i.maxQty && q > i.maxQty) q = i.maxQty;
        const price = resolvePrice(i, q);
        return { ...i, quantity: q, price };
      }),
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const toggleSidebar = useCallback(() => setIsOpen((v) => !v), []);
  const openSidebar = useCallback(() => setIsOpen(true), []);
  const closeSidebar = useCallback(() => setIsOpen(false), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        toggleSidebar,
        openSidebar,
        closeSidebar,
        totalItems,
        subtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
